import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { logger } from '../utils/logger';
import { supabaseService } from './supabase';
import { debounce } from './utils';
import {
  ExtendedRealtimeChannel,
  getChannelSubscriptionCount,
  channelHasBindings,
  channelHasPostgresListener,
} from '../types/realtime-extended';

export interface SubscriptionConfig {
  channelName: string;
  table: string;
  event: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  schema?: string;
  filter?: string;
}

/**
 * Manages Supabase realtime subscriptions across the application
 * Provides subscription sharing and cleanup for React Query integration
 */
class SubscriptionManager {
  private channels: Map<string, ExtendedRealtimeChannel> = new Map();
  private handlers: Map<string, Set<(payload: RealtimePostgresChangesPayload<any>) => void>> =
    new Map();
  private onlineHandler: null | ((this: Window, ev: Event) => void) = null;

  constructor() {
    // Set up online event listener for reconnection handling
    if (typeof window !== 'undefined') {
      // Create the handler
      this.onlineHandler = this.handleOnlineStateChange.bind(this);

      // Register the event listener
      window.addEventListener('online', this.onlineHandler);

      // Log initialization
      logger.realtime('React Query SubscriptionManager initialized');
    }
  }

  /**
   * Handles device online state changes
   * When device comes back online, reconnect all active channels
   * @private
   */
  private handleOnlineStateChange(_event: Event): void {
    // Skip if we're offline
    if (!navigator.onLine) return;

    logger.realtime('Device online - reconnecting subscriptions', {
      channelCount: this.channels.size,
    });

    // Reconnect all channels with a small delay between them
    Array.from(this.channels.entries()).forEach(([channelName, channel], index) => {
      setTimeout(() => {
        if (channel.state !== 'joined') {
          logger.realtime('Reconnecting channel', { channelName });

          try {
            // Resubscribe the channel
            channel.subscribe(status => {
              logger.realtime('Channel reconnection status', {
                channelName,
                status,
              });
            });
          } catch (error) {
            logger.error('Error reconnecting channel', {
              error: error instanceof Error ? error.message : String(error),
              channelName,
            });
          }
        }
      }, index * 250); // 250ms delay between each reconnection
    });
  }

  /**
   * Get a unique key for a subscription configuration
   */
  private getSubscriptionKey(config: SubscriptionConfig): string {
    return `${config.channelName}:${config.table}:${config.event}:${config.schema || 'public'}:${config.filter || ''}`;
  }

  /**
   * Get or create a channel for a given name
   */
  private async getOrCreateChannel(channelName: string): Promise<ExtendedRealtimeChannel> {
    // Check if we have an existing channel
    let channel = this.channels.get(channelName);

    // If channel exists but is in a bad state, clean it up
    if (channel && (channel.state === 'errored' || channel.state === 'leaving')) {
      logger.warn('Channel in bad state, cleaning up', {
        channelName,
        state: channel.state,
      });

      try {
        const supabase = await supabaseService.getClient();
        await channel.unsubscribe();
        supabase.removeChannel(channel);
      } catch (e) {
        // Ignore cleanup errors
        logger.debug('Error during channel cleanup (ignoring)', {
          channelName,
          error: e instanceof Error ? e.message : String(e),
        });
      }

      this.channels.delete(channelName);
      channel = undefined;
    }

    // If no channel or was cleaned up, create a new one
    if (!channel) {
      const supabase = await supabaseService.getClient();

      // First, check if there's a lingering channel in Supabase client
      const existingChannels = supabase.getChannels();
      for (const existingChannel of existingChannels) {
        if (existingChannel.topic === channelName) {
          logger.warn('Found lingering channel in Supabase client, removing', {
            channelName,
            state: existingChannel.state,
          });

          try {
            await existingChannel.unsubscribe();
            supabase.removeChannel(existingChannel);
          } catch (e) {
            // Ignore cleanup errors
            logger.debug('Error removing lingering channel (ignoring)', {
              channelName,
              error: e instanceof Error ? e.message : String(e),
            });
          }
        }
      }

      // Create fresh channel
      channel = supabase.channel(channelName) as ExtendedRealtimeChannel;
      this.channels.set(channelName, channel);

      logger.realtime('Created fresh channel', {
        channelName,
      });
    }

    return channel;
  }

  /**
   * Subscribe to database changes
   * Returns an unsubscribe function
   */
  async subscribe(
    config: SubscriptionConfig,
    handler: (payload: RealtimePostgresChangesPayload<any>) => void
  ): Promise<() => void> {
    const subscriptionKey = this.getSubscriptionKey(config);
    const channel = await this.getOrCreateChannel(config.channelName);

    // Check if this is a new subscription or adding to existing
    const isNewSubscription = !this.handlers.has(subscriptionKey);

    // Initialize handlers set if needed
    if (isNewSubscription) {
      this.handlers.set(subscriptionKey, new Set());

      logger.realtime('Creating new subscription', {
        table: config.table,
        event: config.event,
        filter: config.filter || 'none',
        channelName: config.channelName,
      });

      // Check if this listener already exists to prevent duplicates
      const listenerConfig = {
        event: config.event,
        schema: config.schema || 'public',
        table: config.table,
        filter: config.filter,
      };

      // Check if a similar listener already exists on this channel
      const hasExistingListener = channelHasPostgresListener(channel, listenerConfig);

      if (hasExistingListener) {
        logger.warn('Postgres changes listener already exists, skipping', {
          table: config.table,
          event: config.event,
          filter: config.filter,
          channelName: config.channelName,
        });
      } else {
        // Add the PostgreSQL change listener only once per subscription config
        channel.on(
          'postgres_changes',
          listenerConfig,
          // Use debounce to avoid firing the handler too frequently
          debounce(payload => {
            // Call all handlers registered for this subscription
            const handlers = this.handlers.get(subscriptionKey);
            if (handlers) {
              handlers.forEach(h => h(payload));
            }
          }, 300)
        );
      }
    }

    // Add this handler to the set
    const handlers = this.handlers.get(subscriptionKey)!;
    const wasAlreadySubscribed = handlers.has(handler);
    handlers.add(handler);

    // Only log if this is actually a new handler (not a duplicate)
    if (!wasAlreadySubscribed) {
      if (isNewSubscription) {
        logger.realtime('Added first handler to new subscription', {
          table: config.table,
          event: config.event,
          filter: config.filter || 'none',
          handlerCount: handlers.size,
        });
      } else {
        logger.realtime('Added handler to existing subscription', {
          table: config.table,
          event: config.event,
          filter: config.filter || 'none',
          handlerCount: handlers.size,
        });
      }
    }

    // Only subscribe if channel is not already joined or joining
    if (channel.state !== 'joined' && channel.state !== 'joining') {
      try {
        // Add multiple safety checks to prevent duplicate subscriptions

        // Check 1: Channel internal subscription state
        const subscriptionCount = getChannelSubscriptionCount(channel);
        if (subscriptionCount > 0) {
          logger.warn('Channel already has subscriptions, skipping subscribe', {
            channelName: config.channelName,
            state: channel.state,
            subscriptionCount,
          });
          // Return proper unsubscribe function
          return async () => {
            const handlers = this.handlers.get(subscriptionKey);
            if (handlers) {
              handlers.delete(handler);

              if (handlers.size === 0) {
                this.handlers.delete(subscriptionKey);
              }
            }
          };
        }

        // Check 2: Channel bindings (another indicator of active subscriptions)
        const hasBindings = channelHasBindings(channel);
        if (hasBindings) {
          logger.warn('Channel already has bindings, skipping subscribe', {
            channelName: config.channelName,
            state: channel.state,
            bindingsCount: channel.bindings?.all?.length || 0,
          });
          // Return proper unsubscribe function
          return async () => {
            const handlers = this.handlers.get(subscriptionKey);
            if (handlers) {
              handlers.delete(handler);

              if (handlers.size === 0) {
                this.handlers.delete(subscriptionKey);
              }
            }
          };
        }

        // Check 3: If channel is in a transitional state, wait a bit
        if (channel.state === 'leaving' || channel.state === 'errored') {
          logger.warn('Channel in transitional state, skipping subscribe', {
            channelName: config.channelName,
            state: channel.state,
          });
          // Return proper unsubscribe function
          return async () => {
            const handlers = this.handlers.get(subscriptionKey);
            if (handlers) {
              handlers.delete(handler);

              if (handlers.size === 0) {
                this.handlers.delete(subscriptionKey);
              }
            }
          };
        }

        channel.subscribe(status => {
          const debouncedLogger = debounce((logStatus: string) => {
            // Skip logging if offline
            if (logStatus === 'CHANNEL_ERROR' && !navigator.onLine) {
              return;
            }

            logger.realtime('Subscription status changed', {
              channelName: config.channelName,
              status: logStatus,
              table: config.table,
            });
          }, 5000);

          debouncedLogger(status);
        });
      } catch (error) {
        // Check if this is a duplicate subscription error
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('tried to subscribe multiple times')) {
          logger.warn('Duplicate subscription attempt detected, using existing channel', {
            channelName: config.channelName,
            table: config.table,
            state: channel.state,
          });
          // Return proper unsubscribe function
          return async () => {
            const handlers = this.handlers.get(subscriptionKey);
            if (handlers) {
              handlers.delete(handler);

              if (handlers.size === 0) {
                this.handlers.delete(subscriptionKey);
              }
            }
          };
        }

        logger.error('Failed to set up individual subscription', {
          table: config.table,
          topicId: config.filter?.includes('topic_id') ? config.filter.split('=')[1] : 'unknown',
          error: errorMessage,
        });

        // Try to recover by removing the problematic channel and creating a new one
        this.channels.delete(config.channelName);
        throw error;
      }
    } else {
      logger.realtime('Channel already active, reusing existing subscription', {
        channelName: config.channelName,
        state: channel.state,
        table: config.table,
      });
    }

    // Return unsubscribe function
    return async () => {
      const handlers = this.handlers.get(subscriptionKey);
      if (handlers) {
        handlers.delete(handler);

        logger.realtime('Removed handler from subscription', {
          table: config.table,
          event: config.event,
          remainingHandlers: handlers.size,
        });

        // If no handlers left for this subscription, clean up
        if (handlers.size === 0) {
          this.handlers.delete(subscriptionKey);

          // Check if channel has any other subscriptions
          let hasOtherSubscriptions = false;
          for (const key of this.handlers.keys()) {
            if (
              this.getChannelNameFromSubscriptionKey(key, subscriptionKey) === config.channelName
            ) {
              hasOtherSubscriptions = true;
              break;
            }
          }

          // If no other subscriptions for this channel, unsubscribe and remove
          if (!hasOtherSubscriptions) {
            logger.realtime('Removing channel with no handlers', {
              channelName: config.channelName,
            });

            try {
              channel.unsubscribe();
              const supabase = await supabaseService.getClient();
              supabase.removeChannel(channel);
            } catch (error) {
              logger.error('Error unsubscribing channel', {
                error: error instanceof Error ? error.message : String(error),
                channelName: config.channelName,
              });
            }

            this.channels.delete(config.channelName);
          }
        }
      }
    };
  }

  /**
   * Helper to extract channel name from a subscription key
   * This is used when checking if a channel has other subscriptions
   */
  private getChannelNameFromSubscriptionKey(key: string, _currentKey: string): string {
    // In this implementation, we don't store channel names in the keys
    // So we use an alternative approach to find related subscriptions

    // If we already have the subscription info, we can get the channel name directly
    for (const [k, channel] of this.channels.entries()) {
      if (channel.topic && k.startsWith(key.split(':')[1])) {
        return k;
      }
    }

    // Default fallback - treat each key as its own channel
    return key;
  }

  /**
   * Subscribe to multiple events for a single table
   * Returns an array of subscribe cleanup functions
   */
  async subscribeToEvents(
    table: string,
    events: ('INSERT' | 'UPDATE' | 'DELETE' | '*')[],
    handler: (payload: RealtimePostgresChangesPayload<any>) => void,
    channelNamePrefix?: string
  ): Promise<(() => void)[]> {
    // Use a shared channel name for all events to reduce connections
    const baseChannelName = channelNamePrefix || `${table}-${Date.now().toString(36)}`;

    // Create subscription for each event type
    const cleanupFunctions = await Promise.all(
      events.map(async event => {
        const channelName = `${baseChannelName}-${event}`;

        return this.subscribe(
          {
            channelName,
            table,
            event,
            schema: 'public',
          },
          handler
        );
      })
    );

    // Return combined cleanup function to unsubscribe all
    return cleanupFunctions;
  }

  /**
   * Subscribe to multiple tables with the same event type
   * Returns an array of subscribe cleanup functions
   */
  async subscribeToTables(
    tables: string[],
    event: 'INSERT' | 'UPDATE' | 'DELETE' | '*',
    handler: (payload: RealtimePostgresChangesPayload<any>) => void,
    channelNamePrefix?: string
  ): Promise<(() => void)[]> {
    // Use a shared channel name prefix for all tables
    const baseChannelName = channelNamePrefix || `multi-${Date.now().toString(36)}`;

    // Create subscription for each table
    const cleanupFunctions = await Promise.all(
      tables.map(async table => {
        const channelName = `${baseChannelName}-${table}`;

        return this.subscribe(
          {
            channelName,
            table,
            event,
            schema: 'public',
          },
          handler
        );
      })
    );

    // Return all cleanup functions
    return cleanupFunctions;
  }

  /**
   * Get stats about current subscriptions for debugging
   */
  getStats(): {
    totalChannels: number;
    totalSubscriptions: number;
    handlersByTable: Record<string, number>;
    isOnline: boolean;
  } {
    const handlersByTable: Record<string, number> = {};
    let totalHandlers = 0;

    for (const [key, handlers] of this.handlers.entries()) {
      const table = key.split(':')[1]; // Extract table from key
      const handlerCount = handlers.size;

      handlersByTable[table] = (handlersByTable[table] || 0) + handlerCount;
      totalHandlers += handlerCount;
    }

    return {
      totalChannels: this.channels.size,
      totalSubscriptions: totalHandlers,
      handlersByTable,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    };
  }

  /**
   * Remove all subscriptions and clean up resources
   */
  async removeAllSubscriptions(): Promise<void> {
    // Unsubscribe all channels
    for (const [channelName, channel] of this.channels.entries()) {
      try {
        channel.unsubscribe();
        const supabase = await supabaseService.getClient();
        await supabase.removeChannel(channel);
      } catch (error) {
        logger.error('Error unsubscribing channel during cleanup', {
          error: error instanceof Error ? error.message : String(error),
          channelName,
        });
      }
    }

    // Clear all collections
    this.channels.clear();
    this.handlers.clear();

    // Clean up event listener
    if (this.onlineHandler && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }

    logger.realtime('All subscriptions removed');
  }
}

// Singleton instance
export const subscriptionManager = new SubscriptionManager();
